import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useSlug } from "@/hooks/useSlug";
import {
  CommunityGroupForm,
  type CreateOrEditCommunityGroupFormValues,
} from "@/modules/community-groups/components/community-group-editor/CommunityGroupForm";
import {
  createCommunityGroupFormValues,
  createCommunityGroupRequest,
  getMultiProjectGroupType,
} from "@/modules/community-groups/components/community-group-editor/communityGroupFormValues";
import { communityGroupMutations } from "@/queries/community-groups/mutations";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { routes } from "@/routes";

import { useInterestGroupFormTranslations } from "./useInterestGroupFormTranslations";

export function EditInterestGroupPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const translations = useInterestGroupFormTranslations(true);
  const queries = useCommunityGroupQueries();
  const groupQuery = useQuery({ ...queries.details({ groupId: id! }, { translationMode: "original" }), gcTime: 0 });
  const permissionQueries = usePermissionQueries();
  const defaultsQuery = useQuery(permissionQueries.listDefaultGroups());
  const rolesQuery = useQuery(permissionQueries.listRoles());
  const enableMutation = communityGroupMutations.useEnableGroup();
  const disableMutation = communityGroupMutations.useDeleteGroup();
  const updateMutation = communityGroupMutations.useUpdateGroup();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const group = groupQuery.data;
  async function onSubmit(values: CreateOrEditCommunityGroupFormValues) {
    let data = createCommunityGroupRequest(values);
    if (values.image[0]) {
      const image = await uploadFormImage(values.image[0]);
      data = { ...data, imageId: image?.id };
    }
    if (values.enabled && group?.disabled) {
      await enableMutation.mutateAsync({ id: id!, groupType: values.type });
    } else if (!values.enabled && !group?.disabled) {
      await disableMutation.mutateAsync({ id: id!, groupType: values.type });
    }
    updateMutation.mutate(
      {
        groupId: id!,
        data: { ...data, postingLevel: values.postingLevel },
        isRealEstate: !!values.isRealEstate,
        groupType: values.type,
      },
      { onSuccess: (updated) => void navigate(routes.interestGroups.details({ slug, id: updated.id })) },
    );
  }
  if (group && (!group.canEdit || group.isResidentGroup)) {
    return <ErrorPage status={403} />;
  }
  const error = groupQuery.error || defaultsQuery.error || rolesQuery.error;
  if (error) {
    return <ErrorPage error={error} />;
  }
  if (groupQuery.isFetching || defaultsQuery.isFetching || rolesQuery.isFetching) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <CommunityGroupForm
      contentLanguageId={group?.originalLanguageId}
      type="interest"
      canToggleStatus={!!group?.canDelete && !!group?.systemGroupId}
      isMultiProjectGroup={group?.projectConnectionGroupId !== undefined || group?.systemGroupId !== undefined}
      multiProjectGroupType={getMultiProjectGroupType(group)}
      isEditMode
      defaultValues={createCommunityGroupFormValues({
        roles: rolesQuery.data || [],
        defaultPermissions: defaultsQuery.data || [],
        group,
        type: "interest",
      })}
      isSubmitting={isUploadingImage || updateMutation.isPending}
      translations={translations}
      onSubmit={onSubmit}
    />
  );
}
